"use client";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { FiArrowRight, FiMenu, FiX } from "react-icons/fi";
import { useState, useEffect } from "react";
import logo from "../../images/logo.png";
import Image from "next/image";

const navItems = [
  "Services",
  "Our Impact",
  "About Us",
  "Testimonials",
  "Contact Us",
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.3 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 15 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const mobileMenuVariants = {
  hidden: { opacity: 0, x: "100%" },
  show: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: "100%" },
};

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    document.body.style.overflow = !isMenuOpen ? "hidden" : "auto";
  };

  const slugify = (label: string) =>
    label.toLowerCase().trim().replace(/\s+/g, "-");
  const isContactItem = (label: string) => slugify(label).includes("contact");

  return (
    <header
      className={`header ${scrolled ? "scrolled" : ""} ${
        isMenuOpen ? "menu-open" : ""
      }`}
    >
      {/* animated background layer */}
      <div className="header__bg" aria-hidden />

      <div className="header__container">
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`header__logo ${
            isMenuOpen ? "header__logo--hidden-mobile" : ""
          }`}
        >
          <div className="header__logo-icon">
            <Image
              src={logo}
              alt="Perftecht Solutions Logo"
              width={50}
              height={50}
              priority
            />
          </div>
          <div className="header__logo-text">
            <div className="header__logo-line">
              <span className="header__logo-perf">Perf</span>
              <span className="header__logo-techt">Techt</span>
            </div>
            <span className="header__logo-sub">Solutions</span>
          </div>
        </motion.div>

        {/* Desktop Navigation */}
        <motion.nav
          className="header__nav"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          <ul className="header__nav-list">
            {navItems.map((item) => {
              const contact = isContactItem(item);
              return (
                <motion.li
                  key={item}
                  variants={itemVariants}
                  className="header__nav-item"
                >
                  <a
                    href={`#${slugify(item)}`}
                    className={`header__nav-link ${
                      contact ? "header__nav-contact" : ""
                    }`}
                  >
                    {item}
                  </a>
                </motion.li>
              );
            })}
          </ul>
        </motion.nav>

        {/* Mobile Menu Button */}
        <button
          className="header__menu-button"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          {isMenuOpen ? "" : <FiMenu className="header__menu-icon" />}
        </button>
      </div>

      <section className="hero">
        <div className="hero__container">
          <motion.div
            className="hero__content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <h1 className="hero__title">
              <span className="hero__title-line">Transform Your</span>
              <span className="hero__title-highlight">Digital Strategy</span>
            </h1>

            <p className="hero__subtitle">
              We engineer high-performance digital solutions that drive
              measurable business outcomes
            </p>

            <div className="hero__cta-group">
              <motion.button
                className="hero__cta-primary hero__cta-primary--animated"
                whileHover={{ y: -2, boxShadow: "0 6px 12px rgba(0,0,0,0.10)" }}
                whileTap={{ scale: 0.98 }}
                aria-label="Get Started"
                onClick={() => {
                  const contactSection = document.getElementById("contact-us");
                  if (contactSection)
                    contactSection.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Get Started <FiArrowRight className="hero__cta-icon" />
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            className="hero__graphic"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.7 }}
          >
            <div className="hero__graphic-circle hero__graphic-circle--1"></div>
            <div className="hero__graphic-circle hero__graphic-circle--2"></div>
            <div className="hero__graphic-connection"></div>
          </motion.div>
        </div>
      </section>

      {/* Mobile Navigation — moved OUTSIDE header__container so it never gets blurred */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              className="header__mobile-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMenu}
              aria-hidden
            />
            <motion.nav
              id="mobile-menu"
              className="header__mobile-nav"
              variants={mobileMenuVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              transition={{ type: "spring", damping: 25 }}
              aria-modal="true"
              role="dialog"
            >
              <ul className="header__mobile-list">
                {navItems.map((item, index) => {
                  const contact = isContactItem(item);
                  return (
                    <motion.li
                      key={item}
                      className="header__mobile-item"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{
                        opacity: 1,
                        x: 0,
                        transition: { delay: 0.1 + index * 0.1 },
                      }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <a
                        href={`#${slugify(item)}`}
                        className={`header__mobile-link ${
                          contact ? "header__mobile-link--contact" : ""
                        }`}
                        onClick={toggleMenu}
                      >
                        <motion.span
                          className="header__mobile-link-text"
                          whileHover={{ color: "#3b82f6" }}
                        >
                          {item}
                        </motion.span>
                        {!contact && (
                          <motion.div
                            className="header__mobile-link-highlight"
                            layoutId={`mobileHighlight-${index}`}
                          />
                        )}
                      </a>
                    </motion.li>
                  );
                })}
              </ul>
              <div className="header__mobile-close">
                <button onClick={toggleMenu} aria-label="Close menu">
                  <FiX className="header__mobile-closeIcon" />
                </button>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
